import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";

import { RootState } from "../../redux/store";
import { AppDataState, AppData, ApiError } from "./types/appdata-types";

function filterSectionByCountry(section: any, countryId: number): any {
  if (!section || !section.plans) return section;

  const filteredPlans = (section.plans as any[]).filter(
    (plan: any) =>
      Array.isArray(plan.countries) && plan.countries.includes(countryId),
  );

  const visibleCategoryIds = new Set(
    filteredPlans.map((p: any) => p.category_id),
  );

  const filteredCategories = (section.categories as any[]).filter((cat: any) =>
    visibleCategoryIds.has(cat.id),
  );

  const visibleProviderIds = new Set(
    filteredCategories.map((cat: any) => cat.provider_id),
  );

  const filteredProviders = (section.providers as any[]).filter((p: any) =>
    visibleProviderIds.has(p.id),
  );

  return {
    ...section,
    providers: filteredProviders,
    categories: filteredCategories,
    plans: filteredPlans,
  };
}

function applyCountryFilter(rawData: AppData, countryId: number): AppData {
  const result: AppData = {};

  for (const key of Object.keys(rawData)) {
    const section = rawData[key];

    if (!section || typeof section !== "object" || !section.plans) {
      result[key] = section;
      continue;
    }

    result[key] = filterSectionByCountry(section, countryId);
  }

  return result;
}

const initialState: AppDataState = {
  data: {}, // App data
  loading: false,
  error: null,
  success: false,
  lastFetched: null,
};

const API_URL = process.env.REACT_APP_API_URL;

// ✅ Thunk to fetch backend site-wide data
export const fetchAppData = createAsyncThunk<
  { sections: AppData; selectedCountryId: number | null },
  { silent?: boolean } | void,
  { rejectValue: ApiError; state: RootState }
>("appData/fetch", async (_, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  const selectedCountryId =
    thunkAPI.getState().locale.selectedCountry?.id ?? null;

  try {
    const headers: any = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const res = await axios.get(`${API_URL}/app-data`, { headers });

    // The actual useful data is a JSON string → parse it
    const parsedData =
      typeof res.data.data === "string"
        ? JSON.parse(res.data.data)
        : res.data.data;

    return { sections: parsedData, selectedCountryId };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch app data" },
    );
  }
});

// ✅ Slice
const appDataSlice = createSlice({
  name: "appData",
  initialState,
  reducers: {
    setAppData: (state, action: PayloadAction<AppData>) => {
      state.data = action.payload;
    },
    invalidateAppData: (state) => {
      state.data = {};
      state.success = false;
      state.error = null;
    },
    resetAppData: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.data = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAppData.pending, (state, action) => {
        const isSilent = Boolean(action.meta.arg?.silent);
        state.loading = !isSilent;
        state.error = null;
        if (!isSilent) {
          state.success = false;
        }
      })
      .addCase(fetchAppData.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.lastFetched = Date.now();
        const { sections, selectedCountryId } = action.payload;
        state.data =
          selectedCountryId !== null
            ? applyCountryFilter(sections, selectedCountryId)
            : sections;
      })
      .addCase(fetchAppData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch app data";
      });
  },
});

export const { setAppData, invalidateAppData, resetAppData } =
  appDataSlice.actions;
export default appDataSlice.reducer;
